/**
 * Support and resistance from price action alone.
 *
 * A level is a price where the market has turned before. Not a formula from a
 * textbook — the swing highs and lows of the last three days, clustered where
 * they sit within a fraction of a percent of each other, ranked by how often
 * and how recently price respected them. That is what a discretionary trader
 * draws by hand, and it is what a beginner cannot yet see.
 *
 * Pure: bars in, levels out. Everything here runs in the browser on candles
 * the browser already fetches from Hyperliquid on the user's own IP, so it
 * costs our servers nothing — after the liquidation-sampler incident, that is
 * a requirement rather than a preference.
 */

export interface Candle {
    time: number;
    high: number;
    low: number;
    close: number;
}

export type LevelKind = 'support' | 'resistance';

/** A cluster of swings, before it is oriented against the current price. */
export interface Zone {
    /** touch-weighted mean of the swings in the zone */
    price: number;
    low: number;
    high: number;
    touches: number;
    /** touches weighted by recency; the ranking key */
    strength: number;
    lastTouchAt: number;
}

export interface Level extends Zone {
    kind: LevelKind;
    /** signed distance from spot, in % of spot */
    distancePct: number;
    /** 0..1, log-scaled against the strongest level on screen — drives opacity */
    intensity: number;
}

/**
 * Bars on either side a bar must beat to count as a swing. Five 15-minute
 * bars is 75 minutes each way: three gave 51 swings over three days of BTC,
 * one every ~$57, which is noise, not turning points.
 */
export const SWING_LOOKBACK = 5;
/**
 * The most a zone may span, in % of spot: swings further apart than this are
 * different levels. Measured from spot so a $0.14 coin and a $79k one get the
 * same resolution. A cap on the SPAN, not a distance to a running mean — the
 * mean drifts with every join and chained half the chart into one "level".
 */
export const CLUSTER_TOLERANCE_PCT = 0.3;
/** A touch a day old counts half; three touches yesterday outrank five three days ago. */
export const RECENCY_HALF_LIFE_MS = 24 * 60 * 60 * 1000;
/** Levels kept per side. Beyond this a chart is a ladder of noise. */
export const LEVELS_PER_SIDE = 4;

export interface Swing {
    price: number;
    time: number;
    kind: 'high' | 'low';
}

/**
 * A bar whose high beats the highs of `lookback` bars on either side is a
 * swing high; a low symmetrically. The first and last `lookback` bars cannot
 * qualify — there is nothing on one side to beat.
 */
export function findSwings(
    bars: readonly Candle[],
    lookback = SWING_LOOKBACK,
): Swing[] {
    const swings: Swing[] = [];
    for (let i = lookback; i < bars.length - lookback; i++) {
        const bar = bars[i]!;
        let isHigh = true;
        let isLow = true;
        for (let k = 1; k <= lookback; k++) {
            const before = bars[i - k]!;
            const after = bars[i + k]!;
            if (bar.high <= before.high || bar.high <= after.high) isHigh = false;
            if (bar.low >= before.low || bar.low >= after.low) isLow = false;
            if (!isHigh && !isLow) break;
        }
        if (isHigh) swings.push({ price: bar.high, time: bar.time, kind: 'high' });
        if (isLow) swings.push({ price: bar.low, time: bar.time, kind: 'low' });
    }
    return swings;
}

/**
 * Merge swings into zones. Sorted by price, a swing joins the current zone
 * while the zone's total span stays within the tolerance; otherwise it
 * starts a new one. A zone may hold both highs and lows — a level that was
 * resistance and then support is still one level.
 */
export function clusterSwings(
    swings: readonly Swing[],
    spot: number,
    now: number,
    tolerancePct = CLUSTER_TOLERANCE_PCT,
    halfLifeMs = RECENCY_HALF_LIFE_MS,
): Zone[] {
    if (!Number.isFinite(spot) || spot <= 0) return [];
    const tolerance = (spot * tolerancePct) / 100;
    const sorted = [...swings].sort((a, b) => a.price - b.price);
    const zones: Zone[] = [];
    let current: (Zone & { weightSum: number; weightedPx: number }) | null =
        null;

    const weightOf = (time: number) =>
        Math.pow(0.5, Math.max(0, now - time) / halfLifeMs);

    for (const swing of sorted) {
        const w = weightOf(swing.time);
        if (current && swing.price - current.low <= tolerance) {
            current.weightSum += w;
            current.weightedPx += swing.price * w;
            current.price = current.weightedPx / current.weightSum;
            current.low = Math.min(current.low, swing.price);
            current.high = Math.max(current.high, swing.price);
            current.touches += 1;
            current.strength += w;
            current.lastTouchAt = Math.max(current.lastTouchAt, swing.time);
        } else {
            if (current) zones.push(strip(current));
            current = {
                price: swing.price,
                low: swing.price,
                high: swing.price,
                touches: 1,
                strength: w,
                lastTouchAt: swing.time,
                weightSum: w,
                weightedPx: swing.price * w,
            };
        }
    }
    if (current) zones.push(strip(current));
    return zones;
}

function strip(z: Zone & { weightSum: number; weightedPx: number }): Zone {
    const { weightSum: _w, weightedPx: _p, ...zone } = z;
    return zone;
}

/**
 * Orient zones against the current price: above it is resistance, below it
 * is support, and a zone the price is sitting inside right now is reported
 * on the side its centre falls. Keeps the strongest `perSide` each way and
 * scales intensity logarithmically against the strongest of all, so the
 * second-strongest level is still visible rather than washed out.
 */
export function orientLevels(
    zones: readonly Zone[],
    spot: number,
    perSide = LEVELS_PER_SIDE,
): Level[] {
    if (!Number.isFinite(spot) || spot <= 0 || zones.length === 0) return [];
    const above = zones
        .filter((z) => z.price > spot)
        .sort((a, b) => b.strength - a.strength)
        .slice(0, perSide);
    const below = zones
        .filter((z) => z.price <= spot)
        .sort((a, b) => b.strength - a.strength)
        .slice(0, perSide);
    const chosen = [...above, ...below];
    const max = chosen.reduce((m, z) => Math.max(m, z.strength), 0);
    if (max <= 0) return [];
    return chosen.map((z) => ({
        ...z,
        kind: z.price > spot ? 'resistance' : 'support',
        distancePct: round2(((z.price - spot) / spot) * 100),
        // floor at 1/1000 of the strongest so a weak level still gets a faint band
        intensity: Math.max(
            0,
            1 + Math.log10(Math.max(z.strength / max, 0.001)) / 3,
        ),
    }));
}

/**
 * Bars to zones in one call. The tolerance is scaled from the last close
 * rather than the live tick so the zones themselves do not shift on every
 * price movement; orienting them against the live price is the cheap step
 * (`orientLevels`) and is done separately, on a quantised spot.
 */
export function computeZones(
    bars: readonly Candle[],
    now: number,
    options: { lookback?: number; tolerancePct?: number; halfLifeMs?: number } = {},
): Zone[] {
    if (bars.length < 2 * (options.lookback ?? SWING_LOOKBACK) + 1) return [];
    const spotForScale = bars[bars.length - 1]!.close;
    return clusterSwings(
        findSwings(bars, options.lookback),
        spotForScale,
        now,
        options.tolerancePct,
        options.halfLifeMs,
    );
}

/**
 * The live price, coarsened to 0.1% steps, so re-orienting the levels happens
 * a few times a minute rather than on every tick.
 */
export function quantiseSpot(spot: number): number {
    if (!Number.isFinite(spot) || spot <= 0) return 0;
    // geometric steps: the step is 0.1% of the price at every scale, so the
    // same rung comes back for the same neighbourhood regardless of the coin
    const rung = Math.log(1.001);
    return Math.exp(Math.round(Math.log(spot) / rung) * rung);
}

function round2(v: number): number {
    return Number(v.toFixed(2));
}
